import { useEffect, useState, type ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useLiveQuery } from 'dexie-react-hooks';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { Button } from '@mes-amis/galette-design-system';
import db from '@/db/db';
import { dispatchDataEvent, DataEvents } from '@/db/events';
import { createCaseNoteDraft, updateCaseNoteDraft } from '@/db/services/case_note_service';
import type { CaseNote, NoteOptions } from '@/db/types/case_note';
import { useClientFromParam } from '@/hooks/useClientFromParam';
import { useCurrentPerson } from '@/hooks/useCurrentPerson';
import { isOnline } from '@/lib/connectivity';
import { formToDraftInput, initialFormState, validateCaseNoteForm, type CaseNoteFormErrors, type CaseNoteFormState } from '@/lib/case-note-form';
import CaseNoteForm from '@/components/notes/CaseNoteForm';
import { noteIdFromParam } from '@/components/notes/route';
import { createLogger, LogLevel } from '@/lib/logger';

const logger = createLogger('NoteEdit', LogLevel.INFO);

/** Upload in the background when connected; offline, the next sync picks the note up. */
function uploadSoon(): void {
  if (!isOnline()) return;
  dispatchDataEvent(DataEvents.SYNC_CASE_NOTES).catch((error) => logger.debug('Note upload deferred', error));
}

/** Writes a new note, or edits one that has not uploaded yet. Saving stores it on this device. */
export default function NoteEdit() {
  const navigate = useNavigate();
  const { noteId: noteParam } = useParams<{ noteId?: string }>();
  const { client, clientId, clientSlug, loading } = useClientFromParam();
  const { currentPerson, organizationId } = useCurrentPerson();
  const noteId = noteIdFromParam(noteParam);
  const editing = noteId !== null;

  const options = useLiveQuery(
    async () => (organizationId ? (await db.noteOptions.get(organizationId)) ?? null : undefined),
    [organizationId],
  );
  const existing = useLiveQuery(
    async () => (noteId === null ? null : (await db.caseNotes.get(noteId)) ?? null),
    [noteId],
  );

  const [state, setState] = useState<CaseNoteFormState | null>(null);
  const [errors, setErrors] = useState<CaseNoteFormErrors>({});
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Ask for the options once if this device has never stored them.
  useEffect(() => {
    if (options === null && isOnline()) {
      dispatchDataEvent(DataEvents.REFRESH_NOTE_OPTIONS).catch((error) => logger.debug('Note options refresh skipped', error));
    }
  }, [options]);

  useEffect(() => {
    if (state || !options || existing === undefined) return;
    setState(initialFormState(options, existing));
  }, [state, options, existing]);

  const notesTab = `/clients/${clientSlug ?? ''}?tab=notes`;
  const back = () => navigate(editing && existing ? `/clients/${clientSlug}/notes/${existing.id}` : notesTab);

  const save = async () => {
    if (!state || !options || !clientId || !organizationId || !currentPerson) return;
    const found = validateCaseNoteForm(state, options);
    setErrors(found);
    if (Object.keys(found).length > 0) return;

    setSaving(true);
    setSaveError(null);
    try {
      const author: CaseNote['author'] = existing?.author ?? {
        id: currentPerson.id,
        name: currentPerson.official_full_name || [currentPerson.first_name, currentPerson.last_name].filter(Boolean).join(' '),
      };
      const input = formToDraftInput(state, options, { clientId, organizationId, author });
      if (editing && existing) {
        await updateCaseNoteDraft(existing.id, input);
      } else {
        await createCaseNoteDraft(input);
      }
      uploadSoon();
      navigate(notesTab, { replace: true });
    } catch (error) {
      setSaveError(error instanceof Error ? error.message : 'The note could not be saved on this device.');
      setSaving(false);
    }
  };

  if (loading || options === undefined || (editing && existing === undefined)) {
    return <div className="h-full bg-gray-50" />;
  }

  if (!client) {
    return <Message title="Client not found" body="This client isn't on this device." />;
  }

  if (editing && (!existing || existing.synced)) {
    return (
      <Message
        title="This note can't be edited here"
        body="It's already in Mon Ami. To change it, open the client in Mon Ami."
        action={<Button variant="secondary" onClick={() => navigate(notesTab)}>Back to notes</Button>}
      />
    );
  }

  if (options === null || !hasAnyType(options)) {
    return (
      <Message
        title="Notes aren't ready on this device yet"
        body={options === null
          ? 'Connect to the internet once so Mon Ami GO can download your programs and note settings. After that you can write notes offline.'
          : "Your account can't add notes for this organization. Ask your administrator if you need to."}
        action={<Button variant="secondary" onClick={() => navigate(notesTab)}>Back to notes</Button>}
      />
    );
  }

  return (
    <div className="h-full bg-gray-50">
      <div className="sticky top-0 z-10 bg-white border-b border-gray-200">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center justify-between gap-3">
          <Button variant="tertiary" size="sm" onClick={back} disabled={saving}>Cancel</Button>
          <h1 className="text-base font-semibold text-gray-900">{editing ? 'Edit note' : 'New note'}</h1>
          <Button variant="primary" size="sm" onClick={() => void save()} loading={saving} disabled={!state}>Save</Button>
        </div>
      </div>
      <div className="max-w-2xl mx-auto px-4 py-5 space-y-4">
        {saveError && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded" role="alert">{saveError}</div>
        )}
        {state && <CaseNoteForm state={state} options={options} errors={errors} onChange={setState} />}
        <p className="text-xs text-gray-500">Saved on this device. It uploads to Mon Ami when you&apos;re online.</p>
      </div>
    </div>
  );
}

function hasAnyType(options: NoteOptions): boolean {
  return options.note_types.length > 0 && (!options.note_types.includes('CaseNote') || options.programs.length > 0 || options.note_types.includes('BasicNote'));
}

function Message({ title, body, action }: { title: string; body: string; action?: ReactNode }) {
  return (
    <div className="h-full bg-gray-50 flex items-center justify-center p-6">
      <div className="text-center max-w-md" data-testid="note-edit-message">
        <ExclamationTriangleIcon className="mx-auto h-12 w-12 text-gray-400 mb-4" />
        <h2 className="text-xl font-semibold text-gray-900 mb-2">{title}</h2>
        <p className="text-gray-600 mb-4">{body}</p>
        {action}
      </div>
    </div>
  );
}
